import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{
    error: Error | null;
    data: any | null;
  }>;
  signIn: (email: string, password: string) => Promise<{
    error: Error | null;
    data: any | null;
  }>;
  signOut: () => Promise<void>;
  resendConfirmation: (email: string) => Promise<{
    error: Error | null;
    data: any | null;
  }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        console.log('Initial session check:', data.session ? 'Session found' : 'No session');
        if (error) {
          console.error('Error getting initial session:', error);
        }
        setSession(data.session);
        setUser(data.session?.user ?? null);
      } catch (error) {
        console.error('Exception getting initial session:', error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (_event, session) => {
          console.log('Auth state changed:', _event, session ? 'Session exists' : 'No session');
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up auth state change listener:', error);
      setLoading(false);
      return () => {};
    }
  }, []);

  const signUp = async (email: string, password: string) => {
    console.log('Signing up with email:', email);
    
    // Get the current URL for the redirect
    const currentUrl = window.location.href;
    // Extract the base URL (domain)
    const baseUrl = window.location.origin;
    
    // Use the deployed URL for the redirect
    const redirectTo = `${baseUrl}/confirm`;
    
    console.log('Using redirect URL:', redirectTo);
    
    try {
      const result = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: redirectTo
        }
      });
      
      console.log('Sign up result:', result.error ? 'Error' : 'Success');
      return result;
    } catch (error) {
      console.error('Sign up error:', error);
      return { error: error as Error, data: null };
    }
  };

  const signIn = async (email: string, password: string) => {
    console.log('Signing in with email:', email);
    try {
      const result = await supabase.auth.signInWithPassword({ email, password });
      console.log('Sign in result:', result.error ? 'Error' : 'Success');
      return result;
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: error as Error, data: null };
    }
  };

  const signOut = async () => {
    console.log('Signing out');
    try {
      await supabase.auth.signOut();
      console.log('Sign out successful');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const resendConfirmation = async (email: string) => {
    console.log('Resending confirmation to:', email);
    
    // Get the current URL for the redirect
    const currentUrl = window.location.href;
    // Extract the base URL (domain)
    const baseUrl = window.location.origin;
    
    // Use the deployed URL for the redirect
    const redirectTo = `${baseUrl}/confirm`;
    
    console.log('Using redirect URL for confirmation:', redirectTo);
    
    try {
      const result = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: redirectTo
        }
      });
      
      console.log('Resend confirmation result:', result.error ? 'Error' : 'Success');
      return result;
    } catch (error) {
      console.error('Resend confirmation error:', error);
      return { error: error as Error, data: null };
    }
  };

  const value = {
    session,
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resendConfirmation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}